import { z } from 'zod'
import { MARKET_GROUPS, type MarketGroup } from '../stockbet/engine/markets.js'
import type { QuotedMarket } from '../stockbet/engine/quote.js'
import type { MatchPhase, MatchStatus } from './matches.js'
import type { TournamentSettings, TournamentStatus } from './tournaments.js'

export const WINNER_SELECTIONS = ['home', 'away'] as const
export type WinnerSelection = (typeof WINNER_SELECTIONS)[number]

/**
 * Ritardo delle puntate su una partita in corso: chi la vede dal vivo non deve battere l'arbitro sul tempo.
 * Passati i 5 secondi la puntata entra solo se la quota è ancora quella vista (altrimenti 409 `oddsChanged`).
 */
export const LIVE_BET_DELAY_MS = 5_000

/** null = esito non offerto in questo momento (troppo probabile o troppo improbabile per il banco). */
export type WinnerOddsPair = { home: number | null; away: number | null }
/** Perché non si può puntare su una partita, in ordine di precedenza. */
export type MarketBlockReason = 'completed' | 'closed' | 'notParticipant' | 'ownTeam'

export type MarketMatch = {
  matchId: string
  phase: MatchPhase
  status: MatchStatus
  round: number
  sortOrder: number
  homeTeam: { id: string; name: string }
  awayTeam: { id: string; name: string }
  homeScore: number | null
  awayScore: number | null
  /** null a partita conclusa (resta il risultato). */
  odds: WinnerOddsPair | null
  closed: boolean
  canBet: boolean
  blockedBy: MarketBlockReason | null
  totalStaked: number
  /** Mercati aperti oltre al vincente (pagina di dettaglio). */
  marketCount: number
}

/** Partite per pagina del palinsesto: "Carica altro" ne aggiunge altrettante. */
export const MARKETS_PAGE_SIZE = 10
export const MARKETS_MAX_LIMIT = 200

/** GET /api/tournaments/:id/markets?limit=: le prime `limit` partite non concluse; fuori dai limiti → 400. */
export const MarketsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(MARKETS_MAX_LIMIT).default(MARKETS_PAGE_SIZE),
})

/** GET /api/tournaments/:id/markets (con o senza X-Guest-Token): partite non concluse, le live in cima, poi per fase in ordine di turno. */
export type MarketsResponse = {
  /** Con i limiti della schedina: il frontend mostra avvisi e vincita con le stesse regole del server. */
  tournament: { id: string; name: string; status: TournamentStatus } & Pick<TournamentSettings, 'maxBet' | 'stockbetMaxLegs' | 'stockbetMaxColumns' | 'stockbetMaxPayout' | 'stockbetBonus'>
  guest: { participantId: string; name: string; teamId: string | null; balance: number } | null
  matches: MarketMatch[]
  /** Tutte le partite non concluse: più di `matches.length` → "Carica altro". */
  total: number
}

/** GET /api/tournaments/:id/stockbet/matches/:matchId?group=: un gruppo sconosciuto → 400. */
export const StockbetMatchQuerySchema = z.object({ group: z.enum(MARKET_GROUPS).default(MARKET_GROUPS[0]) })

/** GET /api/tournaments/:id/stockbet/matches/:matchId (con o senza X-Guest-Token): i mercati di un gruppo di una partita. */
export type StockbetMatchResponse = {
  tournament: MarketsResponse['tournament']
  guest: MarketsResponse['guest']
  match: MarketMatch
  cups: number
  players: { home: { id: string; name: string }[]; away: { id: string; name: string }[] }
  group: MarketGroup
  /** Solo quelli del gruppo chiesto; vuoto a partita conclusa. */
  markets: QuotedMarket[]
  /** Gruppi con almeno un mercato, nell'ordine dei filtri, con il loro conteggio: la barra filtri senza chiederli tutti. */
  groups: { key: MarketGroup; count: number }[]
}

/** GET /api/tournaments/:id/stockbet/outrights (con o senza X-Guest-Token): gli antepost, con torneo e portafoglio per la pagina. */
export type StockbetOutrightsResponse = {
  tournament: MarketsResponse['tournament']
  guest: MarketsResponse['guest']
  /** Vincitore, finalista, capocannoniere; vuoto prima dell'avvio del torneo. */
  markets: QuotedMarket[]
  /** Nomi per le etichette: le squadre e i loro giocatori. */
  teams: { id: string; name: string; players: { id: string; name: string }[] }[]
  /** Una partita del torneo è in corso: le quote la seguono e una giocata con un antepost aspetta il ritardo live. */
  live: boolean
}
